import React, { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "AD_MANAGER", "OPERATIONS"];

  const handleLogin = async () => {
    if (!email.trim() || !password) return;

    setLoading(true);
    setErrorMsg("");
    try {
      const { data } = await API.post("/auth/login", {
        email: email.trim(),
        password,
        // Do NOT send a specific role — the backend authenticates by password
        // and returns the user's actual role.
      });

      const returnedRole: string = data?.data?.user?.role ?? "";
      if (!ADMIN_ROLES.includes(returnedRole)) {
        setErrorMsg("Unauthorized: Only admin accounts can access this portal.");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", data.data.token);
      navigate("/");
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 16,
        background: "hsl(var(--background))",
      }}
    >
      <div
        style={{
          width: "min(440px, 100%)",
          borderRadius: 16,
          border: "1px solid hsl(var(--border))",
          background: "hsl(var(--card))",
          padding: 24,
          color: "hsl(var(--foreground))",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <img
            src="/logo.png"
            alt="divinifyy"
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              objectFit: "contain",
            }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Divinifyy Admin</h1>
            <p style={{ margin: "2px 0 0", color: "hsl(var(--muted-foreground))", fontSize: 12 }}>
              Sign in with your admin credentials
            </p>
          </div>
        </div>

        {errorMsg && (
          <div
            style={{
              marginBottom: 14,
              borderRadius: 12,
              border: "1px solid hsl(var(--destructive) / 0.5)",
              background: "hsl(var(--destructive) / 0.18)",
              padding: "10px 12px",
              fontSize: 12,
              color: "hsl(var(--destructive-foreground))",
            }}
          >
            {errorMsg}
          </div>
        )}

        <label htmlFor="email-input" style={{ display: "block", fontSize: 13, marginBottom: 6, color: "hsl(var(--muted-foreground))" }}>
          Email
        </label>
        <input
          id="email-input"
          type="email"
          placeholder="admin@divinifyy.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          autoFocus
          autoComplete="email"
          style={{
            width: "100%",
            minHeight: 42,
            borderRadius: 12,
            border: "1px solid hsl(var(--input))",
            background: "hsl(var(--secondary))",
            color: "hsl(var(--foreground))",
            padding: "0 12px",
            marginBottom: 14,
          }}
        />

        <label htmlFor="password-input" style={{ display: "block", fontSize: 13, marginBottom: 6, color: "hsl(var(--muted-foreground))" }}>
          Password
        </label>
        <div style={{ position: "relative" }}>
          <input
            id="password-input"
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoComplete="current-password"
            style={{
              width: "100%",
              minHeight: 42,
              borderRadius: 12,
              border: "1px solid hsl(var(--input))",
              background: "hsl(var(--secondary))",
              color: "hsl(var(--foreground))",
              padding: "0 42px 0 12px",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              color: "hsl(var(--muted-foreground))",
              cursor: "pointer",
              padding: 2,
              display: "grid",
              placeItems: "center",
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: "100%",
            marginTop: 18,
            minHeight: 42,
            borderRadius: 12,
            border: "1px solid hsl(var(--primary))",
            background: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          <Lock size={15} /> {loading ? "Signing in..." : "Sign in"}
        </button>
      </div>
    </div>
  );
};

export default Login;
